import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiAuthStaffLoginPost, notifyAuthChanged } from '@/entities/auth';
import {
  decodeUserFromToken,
  getOrCreateDeviceId,
  setStoredUser,
  setTokens,
} from '@/shared/lib/auth';
import { AxiosError } from 'axios';
import { extractErrorMessage } from '@/shared/api/errors';
import { showToast } from '@/shared/hooks/useToast';
import type { LoginValues } from '@/features/auth/modules/types';

type LoginState = {
  isLoading: boolean;
  error: string | null;
};

export const useStaffLogin = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [state, setState] = useState<LoginState>({ isLoading: false, error: null });

  const submit = useCallback(
    async (values: LoginValues) => {
      setState({ isLoading: true, error: null });
      try {
        const data = await apiAuthStaffLoginPost({
          username: values.username.trim(),
          password: values.password,
          device_id: getOrCreateDeviceId(),
        });
        setTokens(data.access_token, data.refresh_token);
        const decoded = decodeUserFromToken(data.access_token);
        if (decoded) {
          setStoredUser({
            ...decoded,
            username: data.user?.username,
            full_name: data.user?.full_name,
          });
        }
        notifyAuthChanged();
        setState({ isLoading: false, error: null });
        showToast({ kind: 'success', message: t('login.toastWelcome') });
        const isOwner = decoded?.role === 'OWNER';
        navigate(isOwner ? '/dashboard' : '/parcels', { replace: true });
      } catch (err) {
        // Auth errors never reveal whether the username or password was wrong.
        const status = err instanceof AxiosError ? err.response?.status : undefined;
        const isAuthError = status === 400 || status === 401 || status === 403 || status === 404;
        const message = isAuthError
          ? t('login.errors.invalidCredentials')
          : extractErrorMessage(err, t('login.errors.generic'));
        setState({ isLoading: false, error: message });
      }
    },
    [navigate, t],
  );

  return { ...state, submit };
};
